import { useCallback, useEffect, useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'

import { Sensor } from '@/features/dashboard/models'
import {
    plantsAtom,
    devicesAtom,
    projectsAtom,
    areasAtom,
    selectedPlantAtom,
    selectedDeviceAtom,
    selectedProjectAtom,
    scannedDataAtom,
    inputValuesAtom,
    sensorDataAtom,
    activeStepAtom,
    showErrorSelectionBarAtom,
    currentPlantAtom,
    isOfflineAtom,
} from '@/core/store/globalStore'
import { AreaDto, DeviceDto, ExistingImageDto, PlantDto, ProjectDto } from '@/core/models/types'
import { useGetUserSettings } from '@/core/hooks-and-services/userSettingsService'
import { useGetAreasService, useGetProjectsService } from '@/core/hooks-and-services/onboardedSensorsService'
import { getDeviceByVendorSerialNumber } from '@/api/apiService'
import { useSensorImages } from './useSensorImages'

// Owns the plant/device/project/area selection and per-sensor form data shown across steps 0-3.
// Reads the same jotai atoms as useOnboardingStepper so both hooks stay in sync without passing data between them.
export const useOnboardingStepContent = ({
    showMenu,
    plants,
    devices,
    sensorToEdit,
}: {
    showMenu: boolean
    plants: PlantDto[]
    devices: DeviceDto[]
    sensorToEdit: Sensor | null
}) => {
    const [plantList, setPlants] = useAtom(plantsAtom)
    const [deviceList, setDevices] = useAtom(devicesAtom)
    const [projectList, setProjects] = useAtom(projectsAtom)
    const [areaList, setAreas] = useAtom(areasAtom)

    const [selectedPlant, setSelectedPlant] = useAtom(selectedPlantAtom)
    const [selectedDevice, setSelectedDevice] = useAtom(selectedDeviceAtom)
    const [selectedProject, setSelectedProject] = useAtom(selectedProjectAtom)
    const currentPlant = useAtomValue(currentPlantAtom)

    const [inputValues, setInputValues] = useAtom(inputValuesAtom)
    const sensorData = useAtomValue(sensorDataAtom)
    const setScannedData = useSetAtom(scannedDataAtom)
    const setActiveStep = useSetAtom(activeStepAtom)
    const setShowErrorSelectionBar = useSetAtom(showErrorSelectionBarAtom)
    const isOffline = useAtomValue(isOfflineAtom)

    const { data } = useGetUserSettings()
    const { data: loadedProjects } = useGetProjectsService(selectedPlant?.shortName || '')
    const projects = loadedProjects ?? []
    const { data: areas = [] } = useGetAreasService(selectedPlant?.shortName || '')

    // Images are no longer part of the sensor list payload, so the edit form has to load them separately.
    // Until they arrive ExistingImages is empty, and saving that would delete every stored image.
    const { data: sensorImages, isLoading: isLoadingSensorImages } = useSensorImages(sensorToEdit)
    const appliedImagesForSensor = useRef<string | null | undefined>(null)
    const pendingProjectCode = useRef<string | null>(null)

    useEffect(() => {
        if (JSON.stringify(plantList) !== JSON.stringify(plants)) setPlants(plants)
        const filteredDevices = devices.filter((device) => device.selectable)
        if (JSON.stringify(deviceList) !== JSON.stringify(filteredDevices)) setDevices(filteredDevices)
        if (JSON.stringify(projectList) !== JSON.stringify(projects)) setProjects(projects)
        if (JSON.stringify(areaList) !== JSON.stringify(areas)) setAreas(areas)
    }, [plants, devices, projects, areas, plantList, deviceList, projectList, areaList])

    useEffect(() => {
        // Prefer the app-wide `currentPlant`; when the user is in "All plants" mode, auto-select
        // the first authorized plant so sensor onboarding always targets a specific plant.
        const preferredShortName =
            selectedPlant?.shortName ?? currentPlant?.shortName ?? data?.plant ?? plantList?.[0]?.shortName

        const foundPlant = plantList?.find((plant) => plant.shortName === preferredShortName) ?? null
        if (foundPlant?.shortName !== selectedPlant?.shortName) {
            setSelectedPlant(foundPlant)
        }
    }, [data, plantList, selectedPlant, currentPlant])

    useEffect(() => {
        if (showMenu && !sensorToEdit && currentPlant) {
            setSelectedPlant(currentPlant)
        }
    }, [showMenu, sensorToEdit, currentPlant, setSelectedPlant])

    useEffect(() => {
        pendingProjectCode.current = sensorToEdit?.project ?? null
        if (sensorToEdit) {
            appliedImagesForSensor.current = null
            setInputValues({
                SuperiorTag: sensorToEdit.superiorTag || '',
                Area: sensorToEdit.area || '',
                Coordinates: sensorToEdit.coordinates || '',
                Tag: sensorToEdit.tag || '',
                VendorSerialNumber: sensorToEdit.vendorSerialNumber || '',
                ImageFiles: sensorToEdit.imageFiles || [],
                ImageDescriptions: sensorToEdit.imageDescriptions || [],
                UserAddedDescription: sensorToEdit.userAddedDescription || '',
                CoordinatesNotAvailable: sensorToEdit.coordinatesNotAvailable || false,
                SensorDbKey: sensorToEdit.sensorDbKey,
                ExistingImages: [],
            })
            const foundPlant =
                plantList?.find(
                    (plant) => plant.shortName === sensorToEdit.plant && plant.plantId === sensorToEdit.plantId
                ) ?? null
            setSelectedPlant(foundPlant)
            const foundDevice = devices?.find((device) => device.key === sensorToEdit.sensorModel) ?? null
            setSelectedDevice(foundDevice)
            setSelectedProject(null)
            setActiveStep(0)
        }
    }, [sensorToEdit, setInputValues])

    useEffect(() => {
        if (!loadedProjects) return
        if (pendingProjectCode.current !== null) {
            if (selectedPlant?.shortName !== sensorToEdit?.plant) return
            const foundProject = loadedProjects.find((project) => project.projectCode === pendingProjectCode.current)
            if (!foundProject) return
            pendingProjectCode.current = null
            setSelectedProject(foundProject)
            return
        }
        if (selectedProject == null) return
        const foundProject =
            loadedProjects.find((project) => project.projectCode === selectedProject.projectCode) ?? null
        if (foundProject?.projectCode !== selectedProject.projectCode) {
            setSelectedProject(foundProject)
        }
    }, [loadedProjects, selectedPlant?.shortName, sensorToEdit, selectedProject, setSelectedProject])

    // Applied once per edited sensor so it cannot clobber edits the user makes after the fetch resolves.
    useEffect(() => {
        if (!sensorToEdit?.sensorDbKey || !sensorImages) return
        if (appliedImagesForSensor.current === sensorToEdit.sensorDbKey) return
        appliedImagesForSensor.current = sensorToEdit.sensorDbKey
        setInputValues((prevValues) => ({
            ...prevValues,
            ExistingImages: sensorImages,
        }))
    }, [sensorToEdit?.sensorDbKey, sensorImages, setInputValues])

    const handleScan = useCallback(
        (payload: any) => {
            setScannedData(payload)
        },
        [setScannedData]
    )

    // Best effort: pre-provisioned serial numbers know their sensor model, so the device can be preselected.
    // A miss (unknown serial, failed request) just leaves the device for the user to pick.
    const resolveDeviceFromVendorSerialNumber = useCallback(
        async (vendorSerialNumber: string) => {
            // Offline the lookup can only hit a stale cache entry, so the device is always picked manually.
            if (isOffline) return
            if (!vendorSerialNumber.trim()) return
            try {
                const device = await getDeviceByVendorSerialNumber(vendorSerialNumber.trim())
                if (!device) return
                setSelectedDevice(deviceList.find((d) => d.key === device.key) ?? device)
                setShowErrorSelectionBar(false)
            } catch {
                return
            }
        },
        [deviceList, isOffline, setSelectedDevice, setShowErrorSelectionBar]
    )

    const handleSelectionChange = useCallback(
        (type: 'plant' | 'device' | 'project' | 'area', changes: { selectedItems: unknown[] }) => {
            const selectedItem = changes.selectedItems[0] ?? null
            if (selectedItem) {
                if (type === 'plant') {
                    pendingProjectCode.current = null
                    setSelectedPlant(selectedItem as PlantDto)
                    setShowErrorSelectionBar(false)
                }
                if (type === 'device') {
                    setSelectedDevice(selectedItem as DeviceDto)
                    setShowErrorSelectionBar(false)
                }
                if (type === 'project') {
                    pendingProjectCode.current = null
                    setSelectedProject(selectedItem as ProjectDto)
                    setShowErrorSelectionBar(false)
                }
                if (type === 'area') {
                    const area = selectedItem as AreaDto
                    setInputValues((prev) => ({ ...prev, Area: area.areaCode as string }))
                }
            }
        },
        [setSelectedPlant, setSelectedDevice, setSelectedProject, setInputValues]
    )

    const handleClear = useCallback(
        (type: 'plant' | 'device' | 'project' | 'area') => {
            switch (type) {
                case 'plant':
                    pendingProjectCode.current = null
                    setSelectedPlant(null)
                    break
                case 'device':
                    setSelectedDevice(null)
                    break
                case 'project':
                    pendingProjectCode.current = null
                    setSelectedProject(null)
                    break
                case 'area':
                    setInputValues((prev) => ({ ...prev, Area: '' }))
                    break
            }
        },
        [setSelectedPlant, setSelectedDevice, setSelectedProject, setInputValues]
    )

    const handleFormUpdate = useCallback(
        (updatedData: Partial<typeof inputValues>) => {
            setInputValues((prevValues) => ({
                ...prevValues,
                ...updatedData,
            }))
        },
        [setInputValues]
    )

    const handleUpdateExistingImages = useCallback(
        (existingImages: ExistingImageDto[]) => {
            setInputValues((prevValues) => ({
                ...prevValues,
                ExistingImages: existingImages,
            }))
        },
        [setInputValues]
    )

    return {
        plantList,
        deviceList,
        projectList,
        areaList,
        selectedPlant,
        selectedDevice,
        selectedProject,
        inputValues,
        sensorData,
        handleScan,
        handleSelectionChange,
        handleClear,
        handleFormUpdate,
        handleUpdateExistingImages,
        resolveDeviceFromVendorSerialNumber,
        isLoadingSensorImages,
    }
}
